import { useStore } from "@nanostores/preact"
import { JSXInternal } from "preact/src/jsx"
import { useEffect, useRef, useState } from "preact/hooks"

import { SESSION_RECORDING_EVENTS, sendSessionRecordingEvent } from "utils/session-recording"
import {
  profile,
  SubscriptionValue,
  support as supportStore,
} from "components/signup/services/store"
import { TranslationProvider } from "components/signup/components/Translation"
import { getSupport } from "components/signup/services/backend"
import { ErrorScreen } from "components/signup/screens/ErrorScreen"
import { Loading } from "components/signup/screens/LoadingScreen"
import { Support } from "components/signup/services/support"
import { Screens } from "components/signup/services/screens"

import { WeatherSummary } from "./components/WeatherSummary"
import { Subscriptions } from "./screens/Subscriptions"
import css from "./style.module.scss"

const subscriptionManagementPath = "/profile/subscription"

function sessionRecordingEventForIncentiveClick(target: EventTarget | null): string {
  if (!(target instanceof Element)) return SESSION_RECORDING_EVENTS.incentivePanelClick

  const marked = target.closest<HTMLElement>("[data-session-recording]")
  if (marked?.dataset.sessionRecording === "continue") {
    return SESSION_RECORDING_EVENTS.incentiveContinueClick
  }
  if (marked?.dataset.sessionRecording === "offer") {
    return SESSION_RECORDING_EVENTS.incentiveOfferClick
  }

  return SESSION_RECORDING_EVENTS.incentivePanelClick
}

export function Incentive({
  onReload,
  onOpenModal,
}: {
  onReload: () => void
  onOpenModal: (page: Screens) => void
}) {
  const [page, setPage] = useState<Screens>(Screens.LoadingScreen)
  const [errorMessage, setErrorMessage] = useState("")
  const support = useStore(supportStore)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return undefined

    const clickListener = (event: Event) => {
      sendSessionRecordingEvent(sessionRecordingEventForIncentiveClick(event.target))
    }
    panel.addEventListener("click", clickListener)

    return () => panel.removeEventListener("click", clickListener)
  }, [])

  useEffect(() => {
    setErrorMessage("")
    ;(async () => {
      let data: Support
      try {
        data = await getSupport()
      } catch (error) {
        let msg = "fetching support, but the error is unknown"
        if (error instanceof Error) msg = error.message

        setPage(Screens.ErrorScreen)
        setErrorMessage(msg)

        return
      }
      supportStore.set(data)
      profile.setKey("source", "incentive")
      profile.setKey("countryIso", data.default_iso_country)
      profile.setKey("premium", data.user.premium)
      profile.setKey("legacy12day", data.user.legacy12day)
      profile.setKey("authorized", data.user.signedIn)
      profile.setKey("emailVerified", data.user.emailVerified)
      profile.setKey("email", data.user.email)
      profile.setKey("paymentProblem", data.user.paymentProblem)
      // console.log(data)
      // console.log(profile.get())
      setPage(Screens.SubscriptionsScreen)
    })()
  }, [])

  let el: JSXInternal.Element
  switch (page) {
    case Screens.LoadingScreen:
      el = (
        <div>
          <Loading />
        </div>
      )
      break
    case Screens.ErrorScreen:
      el = (
        <div>
          <ErrorScreen message={errorMessage} onReload={onReload} />
        </div>
      )
      break
    case Screens.SubscriptionsScreen:
      el = !support ? (
        <Loading />
      ) : (
        <>
          <WeatherSummary />
          <Subscriptions
            experiment={support.experiment}
            onSubmit={(v: SubscriptionValue) => {
              if (support.user.paymentProblem) {
                window.location.assign(subscriptionManagementPath)
                return
              }

              profile.setKey("subscription", v)
              onOpenModal(Screens.LoadingScreen)
            }}
          />
        </>
      )
      break
    default:
      throw new Error(`Unexpected error. Specified page (${page}) is not supported.`)
  }

  return (
    <TranslationProvider definitions={support?.messages}>
      <div className={css.incentive} ref={panelRef}>
        {el}
      </div>
    </TranslationProvider>
  )
}
